import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, MapPin, CreditCard, Clock, ShoppingCart, MessageSquare, Navigation, MessageCircle, AlertTriangle, CheckCircle2, ExternalLink, Copy, Check, ChevronDown, RotateCcw, RefreshCw, UserRound, Landmark, TicketPercent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OrderStatusBadge } from './order-status-badge';
import { ordersClient, getForwardStatus, canCancel, type Order, type OrderStatus, type OrderPaymentAttempt } from '@/lib/orders-client';
import { TransferVerificationDisplay } from './transfer-verification-display';
import { getDashboardUrl } from '@/components/theme-provider';
import { format } from 'date-fns';

// Backend payment-preference enum → 'orders' locale key (payment.methods.*).
const PAYMENT_METHOD_KEYS: Record<string, string> = {
    cash: 'cash',
    send_pos_for_card: 'sendPosForCard',
    bank_transfer: 'bankTransfer',
    mercado_pago: 'mercadoPago',
    card: 'card',
    pay_at_store: 'payAtStore',
};

/**
 * What the items came to before the coupon came off.
 *
 * Derived rather than read off the order: `order.total` is already net of the discount, the delivery
 * fee and the tip, and no pre-discount figure is persisted. Line subtotals already include their
 * option deltas, so summing them is the whole of it.
 */
function itemsSubtotal(order: Order): number {
    return order.items.reduce((sum, item) => sum + Number(item.subtotal), 0);
}

// pending → confirm, confirmed → complete (mirrors orders-client's getForwardActionLabel).
function forwardActionKey(status: OrderStatus): string | null {
    if (status === 'pending') return 'actions.confirm';
    if (status === 'confirmed') return 'actions.complete';
    return null;
}

interface OrderDetailPanelProps {
    order: Order;
    onClose: () => void;
    onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
    onViewConversation?: (conversationId: string) => void;
    /** Retry the POS push of a paid order parked in pos_push_failed. */
    onRetryPosPush?: (orderId: string) => Promise<void>;
    /** Open the customer's profile (addresses + order history). Omit to keep the name static. */
    onViewCustomer?: () => void;
    /** Open the bank transfer that paid this order. Omit to hide the action. */
    onViewTransfer?: () => void;
    /** Rendered inside a dialog (customer profile, transfers page) instead of as the side panel. */
    embedded?: boolean;
}

export function OrderDetailPanel({ order, onClose, onStatusChange, onViewConversation, onRetryPosPush, onViewCustomer, onViewTransfer, embedded }: OrderDetailPanelProps) {
    const { t } = useTranslation('orders');
    const dashboardUrl = getDashboardUrl();
    const isPosPushFailed = order.status === 'pos_push_failed';
    const actionKey = forwardActionKey(order.status);
    return (
        <div
            className={`${
                embedded
                    ? // Sized by the host dialog's flex column. Not `h-full`: a percentage height
                      // against an auto-height dialog collapses, which kills the inner scroll.
                      'w-full min-h-0 flex-1'
                    : // Mobile: cover the order list as a full overlay; desktop: fixed side column.
                      'absolute inset-0 z-20 w-full h-full md:static md:z-auto md:w-96 md:shrink-0 md:border-l md:border-border'
            } bg-card flex flex-col overflow-hidden`}
        >
            {/* Header */}
            <div className="h-14 border-b border-border flex items-center justify-between px-4 shrink-0">
                <div className="flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-sm text-foreground">
                        {t('detail.title', { id: order.id.slice(0, 8) })}
                    </span>
                    {order.isSandbox && (
                        <span className="text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                            {t('sandboxBadge')}
                        </span>
                    )}
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8" aria-label={t('detail.closePanel')}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {/* Status */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{t('detail.status')}</span>
                        <OrderStatusBadge status={order.status} />
                    </div>
                    {/* pos_push_failed is resolved through its dedicated action card below. */}
                    {!isPosPushFailed && (actionKey || canCancel(order.status)) && (
                        <div className="flex gap-2">
                            {actionKey && (
                                <Button
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => onStatusChange(order.id, getForwardStatus(order)!)}
                                >
                                    {t(actionKey)}
                                </Button>
                            )}
                            {canCancel(order.status) && (
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => onStatusChange(order.id, 'cancelled')}
                                >
                                    {t('actions.cancel')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Paid but never registered with the POS — guided manual resolution */}
                {isPosPushFailed && (
                    <PosPushFailedActionCard
                        order={order}
                        dashboardUrl={dashboardUrl}
                        onStatusChange={onStatusChange}
                        onRetryPosPush={onRetryPosPush}
                        onViewConversation={onViewConversation}
                    />
                )}

                {/* Customer */}
                <div className="space-y-1">
                    <span className="text-sm font-medium text-muted-foreground">{t('detail.customer')}</span>
                    {onViewCustomer ? (
                        <button
                            type="button"
                            onClick={onViewCustomer}
                            className="block w-full text-left group"
                            title={t('detail.viewCustomerProfile')}
                        >
                            <div className="text-sm text-foreground group-hover:underline">
                                {order.customer?.name || t('list.noName')}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {order.customer?.phone}
                            </div>
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 group-hover:text-blue-800 dark:group-hover:text-blue-300">
                                <UserRound className="h-3 w-3" />
                                {t('detail.viewCustomerProfile')}
                            </span>
                        </button>
                    ) : (
                        <>
                            <div className="text-sm text-foreground">
                                {order.customer?.name || t('list.noName')}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {order.customer?.phone}
                            </div>
                        </>
                    )}
                </div>

                {/* Items */}
                <div className="space-y-2">
                    <span className="text-sm font-medium text-muted-foreground">{t('detail.items')}</span>
                    <div className="space-y-1.5">
                        {order.items.map((item, idx) => (
                            <div key={idx}>
                                <div className="flex justify-between text-sm">
                                    <span className="text-foreground">
                                        {item.quantity}x {item.name}
                                    </span>
                                    <span className="text-muted-foreground">
                                        {order.currency} {Number(item.subtotal).toFixed(2)}
                                    </span>
                                </div>
                                {item.options && item.options.length > 0 && (
                                    <div className="text-xs text-muted-foreground ml-5 space-y-0.5">
                                        {Object.entries(
                                            item.options.reduce<Record<string, typeof item.options>>((acc, opt) => {
                                                (acc[opt.groupName] ??= []).push(opt);
                                                return acc;
                                            }, {})
                                        ).map(([groupName, opts]) => (
                                            <div key={groupName}>
                                                <span className="font-medium text-muted-foreground">{groupName}: </span>
                                                {opts.map((o, i) => (
                                                    <span key={i}>
                                                        {o.optionName}
                                                        {o.priceDelta !== 0 && (
                                                            <span className="text-muted-foreground/70"> ({o.priceDelta > 0 ? '+' : ''}{o.priceDelta.toFixed(2)})</span>
                                                        )}
                                                        {i < opts.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {item.comments && (
                                    <div className="text-xs text-muted-foreground italic ml-5">
                                        {typeof item.comments === 'string'
                                            ? item.comments
                                            : item.comments.map((c, i) => (
                                                <div key={i}>
                                                    {c.quantity}x {c.text}
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Order-wide notes — kept next to the items they qualify, not buried under the total */}
                {order.notes && (
                    <div className="space-y-1">
                        <span className="text-sm font-medium text-muted-foreground">{t('detail.notes')}</span>
                        <div className="text-sm text-muted-foreground">{order.notes}</div>
                    </div>
                )}

                {/* Delivery */}
                {order.deliveryType && (
                    <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground/70" />
                            <span className="text-sm font-medium text-muted-foreground">
                                {order.deliveryType === 'delivery' ? t('detail.delivery') : t('detail.pickup')}
                            </span>
                        </div>
                        {order.deliveryAddress && (
                            <DeliveryAddressDisplay address={order.deliveryAddress} />
                        )}
                        {order.deliveryType === 'delivery' && order.deliveryFee != null && (
                            <div className="text-sm text-muted-foreground ml-5">
                                {t('detail.deliveryFee')}
                                {(order.deliveryAddress?.deliveryZoneName as string | undefined)
                                    ? ` (${order.deliveryAddress!.deliveryZoneName as string})`
                                    : ''}
                                :{' '}
                                {Number(order.deliveryFee) > 0
                                    ? `${order.currency} ${Number(order.deliveryFee).toFixed(2)}`
                                    : t('detail.free')}
                            </div>
                        )}
                        {order.estimatedDeliveryMinutes && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground ml-5">
                                <Clock className="h-3 w-3" />
                                {t('detail.estimatedMinutes', { minutes: order.estimatedDeliveryMinutes })}
                            </div>
                        )}
                    </div>
                )}

                {/* Payment */}
                {order.paymentPreference && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                                <CreditCard className="h-3.5 w-3.5 text-muted-foreground/70" />
                                <span className="text-sm text-muted-foreground">
                                    {t('detail.payment')}:{' '}
                                    {PAYMENT_METHOD_KEYS[order.paymentPreference]
                                        ? t(`payment.methods.${PAYMENT_METHOD_KEYS[order.paymentPreference]}`)
                                        : order.paymentPreference}
                                </span>
                            </div>
                            {order.paymentConfirmedAt && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {t('detail.paid')}
                                </span>
                            )}
                        </div>

                        {/* Menteo payment link (awaiting payment) */}
                        {order.status === 'pending_payment' && order.paymentLinkUrl && (
                            <PaymentLinkRow url={order.paymentLinkUrl} deadlineAt={order.paymentDeadlineAt} />
                        )}

                        {/* Cross-links to the dashboard payment + loyalty profile */}
                        {dashboardUrl && (order.menteoPaymentId || order.loyaltyCustomerId) && (
                            <div className="flex flex-col gap-1">
                                {order.menteoPaymentId && (
                                    <a
                                        href={`${dashboardUrl}/payments/transactions/${order.menteoPaymentId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                                    >
                                        <ExternalLink className="h-3 w-3" />
                                        {t('detail.viewPaymentInDashboard')}
                                    </a>
                                )}
                                {order.loyaltyCustomerId && (
                                    <a
                                        href={`${dashboardUrl}/loyalty/customers/${order.loyaltyCustomerId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                                    >
                                        <ExternalLink className="h-3 w-3" />
                                        {t('detail.viewLoyaltyCustomer')}
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Transfer Verification */}
                        {order.paymentPreference === 'bank_transfer' && order.bankTransfer && (
                            <div className="space-y-1.5">
                                <TransferVerificationDisplay
                                    transfer={order.bankTransfer}
                                    currency={order.currency}
                                    getReceiptUrl={() => ordersClient.getTransferReceiptUrl(order.id).then((r) => r.data)}
                                />
                                {/* Same detail (and attribution fixes) the Bank Transfers page shows */}
                                {onViewTransfer && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="ml-5 w-[calc(100%-1.25rem)]"
                                        onClick={onViewTransfer}
                                    >
                                        <Landmark className="h-3.5 w-3.5 mr-1.5" />
                                        {t('detail.viewTransfer')}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Payment Attempts */}
                {order.paymentAttempts && order.paymentAttempts.length > 0 && (
                    <PaymentAttemptsSection attempts={order.paymentAttempts} />
                )}

                {/* Total (tip and coupon are already folded into it — broken out for visibility) */}
                <div className="border-t border-border pt-3 space-y-1">
                    {/* Only shown alongside a coupon: without a discount to subtract, a subtotal line
                        that equals the total is noise. With one, it is what makes the maths legible. */}
                    {order.appliedDiscount && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{t('detail.itemsSubtotal')}</span>
                            <span className="text-muted-foreground">
                                {order.currency} {itemsSubtotal(order).toFixed(2)}
                            </span>
                        </div>
                    )}
                    {order.appliedDiscount && (
                        <div className="flex justify-between text-sm">
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                <TicketPercent className="h-3.5 w-3.5 text-muted-foreground/70" />
                                <span className="font-medium text-foreground">{order.appliedDiscount.code}</span>
                                {/* An applied coupon taking nothing off is the confusing case an
                                    operator has to be able to see, so it is labelled rather than hidden. */}
                                {!(Number(order.discountAmount) > 0) && (
                                    <span className="text-xs text-muted-foreground">
                                        {t('detail.couponNoEffect')}
                                    </span>
                                )}
                            </span>
                            {Number(order.discountAmount) > 0 && (
                                <span className="font-medium text-emerald-700 dark:text-emerald-400">
                                    -{order.currency} {Number(order.discountAmount).toFixed(2)}
                                </span>
                            )}
                        </div>
                    )}
                    {Number(order.tipAmount) > 0 && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{t('detail.tip')}</span>
                            <span className="text-muted-foreground">
                                {order.currency} {Number(order.tipAmount).toFixed(2)}
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span className="font-semibold text-foreground">{t('detail.total')}</span>
                        <span className="font-semibold text-foreground">
                            {order.currency} {Number(order.total).toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* POS Error (the pos_push_failed action card already shows it) */}
                {order.posError && !isPosPushFailed && (
                    <div className="rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 p-3 space-y-1">
                        <div className="flex items-center gap-1.5">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
                            <span className="text-sm font-medium text-red-800 dark:text-red-300">{t('detail.posError')}</span>
                        </div>
                        <div className="text-xs text-red-700 dark:text-red-300">{order.posError}</div>
                    </div>
                )}

                {/* Tracking / External IDs */}
                {(order.trackingUrl || order.externalPosOrderId) && (
                    <div className="space-y-1.5">
                        {order.externalPosOrderId && (
                            <div className="text-xs text-muted-foreground/70">
                                POS: {order.externalPosProvider && `${order.externalPosProvider} — `}#{order.externalPosOrderId}
                            </div>
                        )}
                        {order.trackingUrl && (
                            <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                            >
                                <Navigation className="h-3 w-3" />
                                {t('detail.viewTracking')}
                            </a>
                        )}
                    </div>
                )}

                {/* Dates */}
                <div className="space-y-1 text-xs text-muted-foreground/70">
                    <div>
                        {t('detail.createdAt', {
                            date: format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm'),
                        })}
                    </div>
                    {order.confirmedAt && (
                        <div>
                            {t('detail.confirmedAt', {
                                date: format(new Date(order.confirmedAt), 'dd/MM/yyyy HH:mm'),
                            })}
                        </div>
                    )}
                </div>

                {/* Cancelled reason */}
                {order.cancelledReason && (
                    <div className="space-y-1">
                        <span className="text-sm font-medium text-red-700 dark:text-red-300">
                            {t('detail.cancelledReason')}
                        </span>
                        <div className="text-sm text-red-600 dark:text-red-400">{order.cancelledReason}</div>
                    </div>
                )}

                {/* Link to conversation */}
                {order.conversationId && onViewConversation && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => onViewConversation(order.conversationId!)}
                    >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {t('detail.viewConversation')}
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * Guided resolution for a PAID order that never reached the POS: the operator contacts the
 * customer manually, then retries the push, takes the order manually, marks it as keyed into the
 * POS by hand, or cancels it and refunds the captured payment from the dashboard.
 */
function PosPushFailedActionCard({
    order,
    dashboardUrl,
    onStatusChange,
    onRetryPosPush,
    onViewConversation,
}: {
    order: Order;
    dashboardUrl: string | undefined;
    onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
    onRetryPosPush?: (orderId: string) => Promise<void>;
    onViewConversation?: (conversationId: string) => void;
}) {
    const { t } = useTranslation('orders');
    const [retrying, setRetrying] = useState(false);
    const hasConversation = !!(onViewConversation && order.conversationId);
    const paymentUrl =
        dashboardUrl && order.menteoPaymentId
            ? `${dashboardUrl}/payments/transactions/${order.menteoPaymentId}`
            : null;

    const retry = async () => {
        if (!onRetryPosPush) return;
        setRetrying(true);
        try {
            await onRetryPosPush(order.id);
        } finally {
            setRetrying(false);
        }
    };

    const cancelAndRefund = () => {
        // Open the dashboard payment page first (same-click, so popup blockers allow it) — the
        // refund is confirmed there; cancelling the order here never refunds by itself.
        if (paymentUrl) window.open(paymentUrl, '_blank', 'noopener,noreferrer');
        onStatusChange(order.id, 'cancelled');
    };

    return (
        <div className="rounded-md bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-700 p-3 space-y-2.5">
            <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <span className="text-sm font-semibold text-red-800 dark:text-red-300">{t('posFailed.actionRequired')}</span>
            </div>
            <p className="text-xs text-red-800 dark:text-red-300">
                <Trans
                    i18nKey="posFailed.description"
                    ns="orders"
                    components={{ bold: <span className="font-semibold" /> }}
                />
            </p>
            {order.posError && (
                <div className="rounded bg-red-100/70 dark:bg-red-900/40 border border-red-200 dark:border-red-800/60 px-2 py-1.5 space-y-0.5">
                    <div className="text-[11px] text-red-700 dark:text-red-300 break-words">{order.posError}</div>
                    {order.posErrorPermanent && (
                        <div className="text-[10px] font-medium text-red-800 dark:text-red-300">
                            {t('posFailed.permanentError')}
                        </div>
                    )}
                </div>
            )}
            <p className="text-xs text-red-800 dark:text-red-300">
                {hasConversation ? t('posFailed.step1WithConversation') : t('posFailed.step1')}
                <br />
                {t('posFailed.step2')}
            </p>
            <div className="flex flex-col gap-1.5">
                {onRetryPosPush && (
                    <Button size="sm" onClick={() => void retry()} disabled={retrying}>
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${retrying ? 'animate-spin' : ''}`} />
                        {retrying ? t('posFailed.retrying') : t('posFailed.retry')}
                    </Button>
                )}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStatusChange(order.id, 'pending')}
                    disabled={retrying}
                >
                    {t('posFailed.continueWithoutPos')}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onStatusChange(order.id, 'pushed_to_external')}
                    disabled={retrying}
                >
                    {t('posFailed.alreadyInPos')}
                </Button>
                <Button variant="destructive" size="sm" onClick={cancelAndRefund} disabled={retrying}>
                    {t('posFailed.cancelAndRefund')}
                </Button>
            </div>
            {paymentUrl ? (
                <p className="text-[10px] text-red-700/80 dark:text-red-300">{t('posFailed.refundNoteAutoOpen')}</p>
            ) : (
                <p className="text-[10px] text-red-700/80 dark:text-red-300">{t('posFailed.refundNoteManual')}</p>
            )}
        </div>
    );
}

function DeliveryAddressDisplay({ address }: { address: Record<string, unknown> }) {
    const { t } = useTranslation('orders');
    const addr = address as {
        raw?: string;
        coordinates?: { lat: number; lng: number };
        street?: string;
        neighborhood?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
        instructions?: string;
        source?: string;
    };

    const hasCoordinates = addr.coordinates?.lat != null && addr.coordinates?.lng != null;
    const mapsUrl = hasCoordinates
        ? `https://www.google.com/maps/search/?api=1&query=${addr.coordinates!.lat}%2C${addr.coordinates!.lng}`
        : null;

    const isGeolocation = addr.source === 'geolocation';

    // Build display address from structured fields or fall back to raw
    const displayAddress = addr.raw || [addr.street, addr.neighborhood, addr.city].filter(Boolean).join(', ') || JSON.stringify(address);

    return (
        <div className="ml-5 space-y-1.5">
            {/* Main address */}
            <div className="text-sm text-muted-foreground">{displayAddress}</div>

            {/* Structured details (if geocoded and different from raw) */}
            {isGeolocation && addr.street && addr.city && addr.raw !== addr.street && (
                <div className="text-xs text-muted-foreground/70 space-y-0.5">
                    {addr.neighborhood && (
                        <div>
                            {t('detail.address.neighborhood')}: {addr.neighborhood}
                        </div>
                    )}
                    {addr.zipCode && (
                        <div>
                            {t('detail.address.zipCode')}: {addr.zipCode}
                        </div>
                    )}
                </div>
            )}

            {/* Delivery instructions */}
            {addr.instructions && (
                <div className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded px-2 py-1">
                    <MessageCircle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{addr.instructions}</span>
                </div>
            )}

            {/* Google Maps link */}
            {mapsUrl && (
                <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                >
                    <Navigation className="h-3 w-3" />
                    {t('detail.viewOnMaps')}
                </a>
            )}
        </div>
    );
}

/** Copyable Menteo payment link + its deadline, shown while the order awaits payment. */
function PaymentLinkRow({ url, deadlineAt }: { url: string; deadlineAt?: string }) {
    const { t } = useTranslation('orders');
    const [copied, setCopied] = useState(false);

    const copy = () => {
        void navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 p-2.5 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline truncate"
                    title={url}
                >
                    {url}
                </a>
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={copy} aria-label={t('paymentLink.copy')}>
                    {copied ? <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> : <Copy className="h-3 w-3" />}
                </Button>
            </div>
            {deadlineAt && (
                <div className="text-[11px] text-amber-800 dark:text-amber-300">
                    {t('paymentLink.expires', {
                        date: format(new Date(deadlineAt), 'dd/MM/yyyy HH:mm'),
                    })}
                </div>
            )}
        </div>
    );
}

// labelKey resolves against attempts.events.* in the 'orders' namespace.
const ATTEMPT_EVENT_CONFIG: Record<OrderPaymentAttempt['event'], { labelKey: string; color: string; iconColor: string }> = {
    approved: { labelKey: 'approved', color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300', iconColor: 'text-emerald-500 dark:text-emerald-400' },
    paid: { labelKey: 'paid', color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300', iconColor: 'text-emerald-500 dark:text-emerald-400' },
    declined: { labelKey: 'declined', color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300', iconColor: 'text-red-500 dark:text-red-400' },
    failed: { labelKey: 'failed', color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300', iconColor: 'text-red-500 dark:text-red-400' },
    refunded: { labelKey: 'refunded', color: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300', iconColor: 'text-amber-500 dark:text-amber-400' },
    link_generated: { labelKey: 'linkGenerated', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300', iconColor: 'text-blue-500 dark:text-blue-400' },
};

// Resolves against attempts.methods.* in the 'orders' namespace.
const ATTEMPT_METHOD_KEYS: Record<OrderPaymentAttempt['method'], string> = {
    saved_card: 'savedCard',
    payment_link: 'paymentLink',
};

interface PaymentAttemptGroup {
    /** Log entries backing this attempt, in append order. */
    events: OrderPaymentAttempt[];
    /** The attempt's overall outcome — its most significant event. */
    outcome: OrderPaymentAttempt['event'];
    method: OrderPaymentAttempt['method'];
    at: string;
}

// Most significant first: a refund supersedes paid, paid supersedes approved, etc.
const ATTEMPT_OUTCOME_PRECEDENCE: OrderPaymentAttempt['event'][] = ['refunded', 'paid', 'approved', 'declined', 'failed', 'link_generated'];

/**
 * The backend history is an append-only event log, so one attempt spans several entries
 * (e.g. saved-card 'approved' + 'paid', or 'link_generated' + 'paid'). Entries sharing a
 * linkId (or paymentId, for saved cards) are lifecycle events of the same attempt.
 */
function groupPaymentAttempts(attempts: OrderPaymentAttempt[]): PaymentAttemptGroup[] {
    const byKey = new Map<string, OrderPaymentAttempt[]>();
    attempts.forEach((attempt, idx) => {
        const key = attempt.linkId ?? attempt.paymentId ?? `solo-${idx}`;
        const events = byKey.get(key);
        if (events) events.push(attempt);
        else byKey.set(key, [attempt]);
    });
    return Array.from(byKey.values()).map((events) => {
        const outcome = ATTEMPT_OUTCOME_PRECEDENCE.find((e) => events.some((ev) => ev.event === e)) ?? events[events.length - 1].event;
        return { events, outcome, method: events[0].method, at: events[events.length - 1].at };
    });
}

function PaymentAttemptsSection({ attempts }: { attempts: OrderPaymentAttempt[] }) {
    const { t } = useTranslation('orders');
    const [expanded, setExpanded] = useState(false);

    const groups = groupPaymentAttempts(attempts);
    const failedCount = groups.filter((g) => g.outcome === 'declined' || g.outcome === 'failed').length;
    const successCount = groups.filter((g) => g.outcome === 'approved' || g.outcome === 'paid').length;
    const refundedCount = groups.filter((g) => g.outcome === 'refunded').length;

    const summaryParts: string[] = [];
    if (successCount > 0) summaryParts.push(t('attempts.success', { count: successCount }));
    if (failedCount > 0) summaryParts.push(t('attempts.failed', { count: failedCount }));
    if (refundedCount > 0) summaryParts.push(t('attempts.refunded', { count: refundedCount }));
    const summary =
        summaryParts.length > 0
            ? summaryParts.join(', ')
            : t('attempts.attempt', { count: groups.length });

    const hasFailed = failedCount > 0;

    return (
        <div className={`rounded-md border p-2.5 space-y-2 ${hasFailed ? 'bg-red-50/50 dark:bg-red-950/40 border-red-200 dark:border-red-800/60' : 'bg-muted/30 border-border'}`}>
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center justify-between w-full text-left"
            >
                <div className="flex items-center gap-1.5">
                    <RotateCcw className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span className="text-xs font-medium text-foreground">
                        {t('attempts.title', { count: groups.length })}
                    </span>
                    {hasFailed && (
                        <AlertTriangle className="h-3 w-3 text-red-500 dark:text-red-400" />
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground">{summary}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {expanded && (
                <div className="space-y-1.5 pt-1 border-t border-border/50">
                    {groups.map((group, idx) => {
                        const config = ATTEMPT_EVENT_CONFIG[group.outcome];
                        return (
                            <div key={idx} className="flex items-start gap-2 text-xs">
                                <span className="text-muted-foreground/70 shrink-0 tabular-nums">
                                    {format(new Date(group.at), 'dd/MM HH:mm')}
                                </span>
                                <div className="flex-1 min-w-0 space-y-0.5">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color}`}>
                                            {t(`attempts.events.${config.labelKey}`)}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {t(`attempts.methods.${ATTEMPT_METHOD_KEYS[group.method]}`)}
                                        </span>
                                    </div>
                                    {group.events.length === 1 ? (
                                        group.events[0].detail && (
                                            <div className="text-muted-foreground/70 truncate" title={group.events[0].detail}>
                                                {group.events[0].detail}
                                            </div>
                                        )
                                    ) : (
                                        <div className="border-l border-border/60 pl-2 space-y-0.5">
                                            {group.events.map((event, eventIdx) => (
                                                <div key={eventIdx} className="flex items-center gap-1.5 text-[10px] text-muted-foreground/70">
                                                    <span className="tabular-nums shrink-0">{format(new Date(event.at), 'dd/MM HH:mm')}</span>
                                                    <span className="shrink-0">
                                                        {t(`attempts.events.${ATTEMPT_EVENT_CONFIG[event.event].labelKey}`)}
                                                    </span>
                                                    {event.detail && (
                                                        <span className="truncate" title={event.detail}>— {event.detail}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
